<template>
    <div
        :class="['simple-tooltip-container position-relative', triggerClass]"
        tabindex="0">
        <slot name="trigger" />
        <div
            class="rounded-xs p-100 position-absolute"
            :class="[
                'simple-tooltip-content',
                `simple-tooltip-content--${position}`,
                `simple-tooltip-content--${variant}`,
            ]">
            <div class="simple-tooltip-body font-weight-normal">
                <slot />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
defineProps({
    position: {
        type: String,
        default: 'top',
        validator: (value: string) => ['top', 'bottom', 'left', 'right'].includes(value),
    },
    variant: {
        type: String,
        default: 'dark',
        validator: (value: string) => ['dark', 'light'].includes(value),
    },
    triggerClass: {
        type: String,
        default: '',
    },
    /**
     * Width of the tooltip content
     */
    width: {
        type: String,
        default: '300px',
    },
});
</script>

<style lang="scss">
// Styling for a lightweight/widget-compatible version of EsPopover
@use '@energysage/es-ds-styles/scss/variables';

.simple-tooltip-container {
    cursor: pointer;
    display: inline-block;
}

.simple-tooltip-content {
    box-shadow: 0 1px 6px variables.$gray-900;
    display: none;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    width: 300px;
    z-index: 1000;
}

/* Light variant */
.simple-tooltip-content--light {
    background-color: variables.$white;
    color: variables.$gray-900;
}

/* Dark variant */
.simple-tooltip-content--dark {
    background-color: variables.$blue-900;
    color: variables.$white;
}

/* Position variants */
.simple-tooltip-content--top {
    bottom: 100%;
    left: 50%;
    margin-bottom: 8px;
    transform: translateX(-50%);
}

.simple-tooltip-content--bottom {
    left: 50%;
    margin-top: 8px;
    top: 100%;
    transform: translateX(-50%);
}

.simple-tooltip-content--left {
    margin-right: 8px;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
}

.simple-tooltip-content--right {
    left: 100%;
    margin-left: 8px;
    top: 50%;
    transform: translateY(-50%);
}

/* Merged with the main .simple-tooltip-content selector above */

.simple-tooltip-container:hover .simple-tooltip-content,
.simple-tooltip-container:focus .simple-tooltip-content {
    display: block;
    opacity: 1;
    pointer-events: auto;
}

/* Arrows */
.simple-tooltip-content--top::after {
    border-color: transparent;
    border-style: solid;
    border-width: 5px;
    content: '';
    left: 50%;
    margin-left: -5px;
    position: absolute;
    top: 100%;
}

.simple-tooltip-content--bottom::after {
    border-color: transparent;
    border-style: solid;
    border-width: 5px;
    bottom: 100%;
    content: '';
    left: 50%;
    margin-left: -5px;
    position: absolute;
}

.simple-tooltip-content--left::after {
    border-color: transparent;
    border-style: solid;
    border-width: 5px;
    content: '';
    left: 100%;
    margin-top: -5px;
    position: absolute;
    top: 50%;
}

.simple-tooltip-content--right::after {
    border-color: transparent;
    border-style: solid;
    border-width: 5px;
    content: '';
    margin-top: -5px;
    position: absolute;
    right: 100%;
    top: 50%;
}

/* Arrow colors for light variant */
.simple-tooltip-content--light.simple-tooltip-content--top::after {
    border-color: variables.$white transparent transparent;
}

.simple-tooltip-content--light.simple-tooltip-content--bottom::after {
    border-color: transparent transparent variables.$white;
}

.simple-tooltip-content--light.simple-tooltip-content--left::after {
    border-color: transparent transparent transparent variables.$white;
}

.simple-tooltip-content--light.simple-tooltip-content--right::after {
    border-color: transparent variables.$white transparent transparent;
}

/* Arrow colors for dark variant */
.simple-tooltip-content--dark.simple-tooltip-content--top::after {
    border-color: variables.$blue-900 transparent transparent;
}

.simple-tooltip-content--dark.simple-tooltip-content--bottom::after {
    border-color: transparent transparent variables.$blue-900;
}

.simple-tooltip-content--dark.simple-tooltip-content--left::after {
    border-color: transparent transparent transparent variables.$blue-900;
}

.simple-tooltip-content--dark.simple-tooltip-content--right::after {
    border-color: transparent variables.$blue-900 transparent transparent;
}
</style>
